import { formatDate } from "@/lib/format"
import type { WorkItem } from "@/types/sprint-report"

interface Props {
  items: WorkItem[]
}

const TRIAGE_BUCKET = "Untagged / needs triage"

const CATEGORY_ORDER = [
  "Re-Surfaced",
  "Passed in E2E Round 1",
  "Defect post-E2E",
  "Phase 3 Spillover",
  TRIAGE_BUCKET,
]

function categoryRank(label: string): number {
  const i = CATEGORY_ORDER.indexOf(label)
  return i < 0 ? 98 : i // real-but-unlisted tags before the triage bucket
}

export function NewlyAdded({ items }: Props) {
  // Hide Done items here — this tab is about open/active newly-added work.
  const open = items.filter((it) => it.state_category !== "done")
  if (open.length === 0) {
    return (
      <p className="rounded border border-slate-300 p-6 text-center text-sm text-slate-500">
        No open P1/P2 items created in the last 30 days.
      </p>
    )
  }

  const groups = new Map<string, WorkItem[]>()
  for (const it of open) {
    const primary = it.categories?.[0] ?? TRIAGE_BUCKET
    const arr = groups.get(primary) ?? []
    arr.push(it)
    groups.set(primary, arr)
  }
  const ordered = [...groups.entries()].sort(
    (a, b) => categoryRank(a[0]) - categoryRank(b[0]),
  )
  for (const [, rows] of ordered) {
    rows.sort((a, b) => (b.created_date ?? "").localeCompare(a.created_date ?? ""))
  }

  return (
    <div className="space-y-6">
      <p className="text-[11px] text-slate-500">
        Open P1/P2 items <strong>created in the last 30 days</strong>, grouped by category (from
        ADO tags). Anything not yet triage-tagged falls into{" "}
        <span className="rounded bg-slate-100 px-1 font-semibold text-slate-600">
          {TRIAGE_BUCKET}
        </span>
        .{" "}
        <span className="rounded bg-orange-100 px-1 font-semibold text-orange-700">
          ↩ Re-Surfaced
        </span>{" "}
        = tagged as reopened after being Done.
      </p>
      {ordered.map(([category, rows]) => (
        <section key={category}>
          <h2 className="mb-1 text-sm font-semibold text-header">
            {category} <span className="font-normal text-slate-400">({rows.length})</span>
          </h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200/80">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pri</th>
                  <th>Status</th>
                  <th>Bundle</th>
                  <th>Created</th>
                  <th>Tags</th>
                  <th>Title</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <NewlyAddedRow key={it.id} it={it} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  )
}

function NewlyAddedRow({ it }: { it: WorkItem }) {
  const b = it.bundle
  return (
    <tr className="border-t border-slate-200 align-top hover:bg-slate-50">
      <td className="whitespace-nowrap px-2 py-1">
        <a href={it.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
          {it.id}
        </a>
      </td>
      <td className="whitespace-nowrap px-2 py-1 font-medium">{it.priority.raw}</td>
      <td className="whitespace-nowrap px-2 py-1">
        {it.state}
        {it.re_surfaced && (
          <span className="ml-1 rounded bg-orange-100 px-1 text-[10px] font-semibold text-orange-700">
            ↩ Re-Surfaced
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-2 py-1 text-slate-600">
        {b ? `${b.code ?? b.title} · ${b.state}` : "—"}
      </td>
      <td className="whitespace-nowrap px-2 py-1 text-slate-500">{formatDate(it.created_date)}</td>
      <td className="px-2 py-1">
        {(it.categories ?? []).map((c) => (
          <span
            key={c}
            className="mr-1 inline-block rounded bg-slate-100 px-1 text-[10px] text-slate-600"
          >
            {c}
          </span>
        ))}
      </td>
      <td className="px-2 py-1">
        <a href={it.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:underline">
          {it.title}
        </a>
      </td>
    </tr>
  )
}
